import styles from './LanguagePicker.module.scss';
import cx from 'classnames';

import { Typography } from '@shared/Typography';
import { FC, useEffect, useRef, useState } from 'react';
import { languages, useLanguage } from './lib';

type buttonVariant = 'medium' | 'big';

interface LanguagePickerProps {
  showLanguageCode?: boolean;
  buttonVariant?: buttonVariant;
  isTutorial?: boolean;
  className?: string;
}

interface TriggerButtonProps {
  setDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currentLang: string;
  buttonVariant: buttonVariant;
  className?: string;
}

const TriggerButton: FC<TriggerButtonProps> = ({
  setDropdownOpen,
  currentLang,
  buttonVariant,
  className,
}) => {
  const currentLangName =
    languages.find((lang) => lang.code === currentLang)?.name ?? currentLang;

  return (
    <button
      type="button"
      className={cx(
        styles.lpLanguage,
        {
          [styles[`lpLanguage-${buttonVariant}`]]: true,
        },
        className,
      )}
      onClick={() => setDropdownOpen((prev) => !prev)}
    >
      <div className={styles.lpFlagContainer}>
        <img
          src={`/images/flags/${currentLang}.svg`}
          alt=""
          className={cx(styles.lpFlag, { [styles[`size-${buttonVariant}`]]: true })}
        />
        <Typography
          component={buttonVariant === 'big' ? 'description' : 'h2'}
          variant="primary"
          textTransform="uppercase"
          className={styles.lpLanguageName}
        >
          {buttonVariant === 'big' ? currentLangName : currentLang}
        </Typography>
      </div>
      <img src="/images/down.svg" alt="" className={styles.lpArrow} />
    </button>
  );
};

export const LanguagePicker: FC<LanguagePickerProps> = ({
  buttonVariant = 'medium',
  isTutorial = false,
  className,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { currentLang, setLanguage } = useLanguage();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <div
      className={styles.lpContainer}
      ref={containerRef}
      data-open={dropdownOpen}
      data-tutorial="language-picker"
    >
      <TriggerButton
        setDropdownOpen={setDropdownOpen}
        currentLang={currentLang}
        buttonVariant={buttonVariant}
        className={className}
      />

      {dropdownOpen && (
        <div
          className={cx(styles.lpLanguages, {
            [styles.lpLanguagesTutorial]: isTutorial,
          })}
        >
          {languages.map((language) => (
            <div
              key={language.code}
              className={styles.lpLanguageItem}
              onClick={() => {
                setLanguage(language.code);
                setDropdownOpen(false);
              }}
            >
              <img
                src={`/images/flags/${language.code}.svg`}
                alt=""
                className={styles.lpLanguageItemFlag}
              />
              <Typography
                component="h3"
                variant="primary"
                className={cx(styles.lpLanguageItemText, {
                  [styles[`lang-${language.code}`]]: true,
                })}
              >
                {language.name}
              </Typography>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
